import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCoffee } from "@/context/CoffeeContext";
import { ICON_BTN, BTN_PRIMARY, BTN_GHOST, INPUT } from "@/lib/styles";
import { Coffee, X, Gift, CreditCard, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LEVEL_STYLES = {
  0: "text-emerald-500",
  1: "text-amber-500",
  2: "text-orange-500",
  3: "text-rose-500",
};

export function CoffeeModal({ open, onClose }) {
  const { status, refresh } = useCoffee();
  const [giftEmail, setGiftEmail] = useState("");
  const [giftAmount, setGiftAmount] = useState(1);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantAmount, setGrantAmount] = useState(1);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const gift = async () => {
    if (!giftEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coffee/gift", { to_email: giftEmail.trim(), amount: Number(giftAmount) });
      toast.success(data.message);
      setGiftEmail("");
      setGiftAmount(1);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const grant = async () => {
    if (!grantEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coffee/grant", { email: grantEmail.trim(), amount: Number(grantAmount) });
      toast.success(data.message);
      setGrantEmail("");
      setGrantAmount(1);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const recharge = () => {
    toast.info("Pronto podrás recargar cafés con pago seguro. El Escriba te lo agradecerá ☕");
  };

  const s = status || {};
  const level = s.level ?? 0;

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="coffee-modal">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5 sticky top-0 bg-white dark:bg-slate-900 z-10 pb-2">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2">
            <Coffee size={20} className={LEVEL_STYLES[level]} /> Tus cafés
          </h3>
          <button onClick={onClose} className={ICON_BTN} data-testid="coffee-modal-close"><X size={16} /></button>
        </div>

        {/* Balance card */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-5 mb-5 bg-slate-50 dark:bg-slate-900/50">
          <div className="flex items-end gap-2">
            <span className="font-heading font-light text-5xl tracking-tight" data-testid="coffee-months-left">{s.months_left ?? "–"}</span>
            <span className="font-body text-sm text-slate-500 dark:text-slate-400 mb-1.5">taza(s) de café ≈ meses de uso</span>
          </div>
          <div className="flex gap-1 mt-3">
            {Array.from({ length: Math.min(s.months_left ?? 0, 12) }).map((_, i) => (
              <Coffee key={i} size={16} className="text-amber-500" />
            ))}
          </div>
          <p className={`font-body text-sm mt-4 ${LEVEL_STYLES[level]}`} data-testid="coffee-message">{s.message}</p>
        </div>

        {/* Recharge */}
        <button onClick={recharge} className={`${BTN_PRIMARY} w-full mb-3`} data-testid="coffee-recharge-button">
          <CreditCard size={16} /> Recargar café ({s.price_usd ? `$${s.price_usd}` : "$0.98"} / mes)
        </button>

        {/* Gift */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-4 mt-2">
          <h4 className="font-heading font-medium text-sm flex items-center gap-1.5 mb-2"><Gift size={15} className="text-indigo-500" /> Regalar cafés</h4>
          <p className="font-body text-xs text-slate-500 dark:text-slate-400 mb-3">Invita un café a cualquier usuario de Notacora por su correo.</p>
          <div className="flex gap-2">
            <input className={`${INPUT} flex-1`} placeholder="correo@ejemplo.com" value={giftEmail} onChange={(e) => setGiftEmail(e.target.value)} data-testid="coffee-gift-email" />
            <input type="number" min="1" className={`${INPUT} w-20`} value={giftAmount} onChange={(e) => setGiftAmount(e.target.value)} data-testid="coffee-gift-amount" />
          </div>
          <button onClick={gift} className={`${BTN_GHOST} w-full mt-2`} disabled={busy} data-testid="coffee-gift-button">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Gift size={15} />} Regalar
          </button>
        </div>

        {/* Admin grant */}
        {s.is_admin && (
          <div className="border-t border-slate-200 dark:border-slate-800 pt-4 mt-4">
            <h4 className="font-heading font-medium text-sm flex items-center gap-1.5 mb-2"><ShieldCheck size={15} className="text-emerald-500" /> Admin · acreditar cafés</h4>
            <div className="flex gap-2">
              <input className={`${INPUT} flex-1`} placeholder="correo del usuario" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} data-testid="coffee-grant-email" />
              <input type="number" min="1" className={`${INPUT} w-20`} value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} data-testid="coffee-grant-amount" />
            </div>
            <button onClick={grant} className={`${BTN_GHOST} w-full mt-2`} disabled={busy} data-testid="coffee-grant-button">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <ShieldCheck size={15} />} Acreditar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
